import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../contexts/CombinedAuthContext";
import {
  detectBrowserTimezone,
  listSupportedTimezones,
  formatInUserTz,
  DEFAULT_TIMEZONE,
} from "../utils/datetime";

/**
 * Self-contained timezone preference picker for AccountSettings.
 *
 * Reads users/{uid}.timezone and writes directly via the SDK (firestore.rules
 * already allows owner read/write on their own user doc).
 */
export default function TimezonePreference() {
  const { firebaseUser } = useAuth();
  const [tz, setTz] = useState(DEFAULT_TIMEZONE);
  const [initialTz, setInitialTz] = useState(DEFAULT_TIMEZONE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!firebaseUser?.uid) { setLoading(false); return; }
      try {
        const db = getFirestore();
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        const stored = snap.exists() ? snap.data()?.timezone : null;
        const value = stored || detectBrowserTimezone() || DEFAULT_TIMEZONE;
        if (!cancelled) {
          setTz(value);
          setInitialTz(value);
        }
      } catch (err) {
        console.warn("[TimezonePreference] load failed", err);
        if (!cancelled) {
          const fallback = detectBrowserTimezone() || DEFAULT_TIMEZONE;
          setTz(fallback);
          setInitialTz(fallback);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [firebaseUser?.uid]);

  const onSave = async (e) => {
    e?.preventDefault?.();
    if (!firebaseUser?.uid || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const db = getFirestore();
      const ref = doc(db, "users", firebaseUser.uid);
      try {
        await updateDoc(ref, { timezone: tz, updatedAt: serverTimestamp() });
      } catch {
        // Doc may not exist yet — create it.
        await setDoc(ref, { timezone: tz, updatedAt: serverTimestamp() }, { merge: true });
      }
      setInitialTz(tz);
      setMessage({ kind: "ok", text: "Timezone saved." });
    } catch (err) {
      console.error("[TimezonePreference] save failed", err);
      setMessage({ kind: "err", text: err?.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const options = listSupportedTimezones();
  const dirty = tz !== initialTz;
  const nowPreview = formatInUserTz(new Date(), tz, { dateStyle: "full", timeStyle: "long" });

  return (
    <div className="form-section" style={{ marginTop: 22 }}>
      <h3>Time Zone</h3>
      <p style={{ margin: "0 0 0.5rem", color: "#4b5563", fontSize: "0.88rem" }}>
        Used when scheduling promotion start/end times. All dates are stored in UTC and displayed in your zone.
      </p>

      {loading ? (
        <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>Loading…</p>
      ) : (
        <form onSubmit={onSave} style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
          <select
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            style={{
              padding: "0.5rem 0.7rem",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              minWidth: 260,
              background: "#fff",
              color: "#0f3a5f",
              fontSize: "0.9rem",
            }}
            disabled={saving}
          >
            {options.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={!dirty || saving}
            className="r5-pack-cta"
            style={{ padding: "0.55rem 1rem", opacity: !dirty || saving ? 0.6 : 1 }}
          >
            {saving ? "Saving…" : "Save Timezone"}
          </button>
          <span style={{ color: "#6b7280", fontSize: "0.82rem" }}>
            Current time in zone: <strong>{nowPreview}</strong>
          </span>
          {message && (
            <span style={{ color: message.kind === "ok" ? "#15803d" : "#b91c1c", fontSize: "0.85rem" }}>
              {message.text}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
